import { KebabMenu } from '@modules/common/components/KebabMenu';
import { MenuItemOptions } from '@modules/common/components/KebabMenu/types';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { WisdomChatArchiveModalContent } from '@modules/oracle/components/WisdomChatArchiveModalContent';
import { WisdomChatRenameModalContent } from '@modules/oracle/components/WisdomChatRenameModalContent';
import { WisdomChatShareModalContent } from '@modules/oracle/components/WisdomChatShareModalContent';
import { trackWisdomEvents } from '@modules/oracle/utils';
import { ArchiveRounded, EditRounded, ShareRounded } from '@mui/icons-material';
import { IconButtonProps, Typography } from '@mui/material';
import { Chat } from 'generated/graphql';

interface WisdomChatListItemMenuProps {
  kebabMenuButtonProps?: IconButtonProps;
  onClose?: () => void;
  chat: Omit<Chat, 'responses'>;
}

export const WisdomChatListItemMenu = ({
  kebabMenuButtonProps,
  onClose,
  chat,
}: WisdomChatListItemMenuProps) => {
  const { openModal } = useMuiModalContext();

  const openChatShareModal = () => {
    openModal({
      contentComponent: () => (
        <WisdomChatShareModalContent chatName={chat.title} />
      ),
      modalProps: {
        maxWidth: 'sm',
        fullWidth: true,
        PaperProps: {
          elevation: 3,
        },
      },
    });

    trackWisdomEvents({
      eventName: 'Wisdom - Chat Share Button Clicked',
      chatName: chat.title,
      from: 'Chat List',
    });
  };

  const openChatRenameModal = () => {
    openModal({
      contentComponent: () => <WisdomChatRenameModalContent chat={chat} />,
      modalProps: {
        maxWidth: 'sm',
        fullWidth: true,
        PaperProps: {
          elevation: 3,
        },
      },
    });
    trackWisdomEvents({
      eventName: 'Wisdom - Rename Chat Clicked',
      chatName: chat.title,
      from: 'Chat List',
    });
  };

  const openChatArchiveModal = () => {
    openModal({
      contentComponent: () => <WisdomChatArchiveModalContent chat={chat} />,
      modalProps: {
        maxWidth: 'sm',
        fullWidth: true,
        PaperProps: {
          elevation: 3,
        },
      },
    });

    trackWisdomEvents({
      eventName: 'Wisdom - Chat Archive Button Clicked',
      chatName: chat.title,
      from: 'Chat List',
    });
  };

  const kebabMenuItems: MenuItemOptions = [
    {
      icon: (
        <ShareRounded
          fontSize="small"
          sx={{
            color: 'text.secondary',
          }}
        />
      ),
      label: <Typography>Share</Typography>,
      onClick: openChatShareModal,
    },
    {
      icon: (
        <EditRounded
          fontSize="small"
          sx={{
            color: 'text.secondary',
          }}
        />
      ),
      label: <Typography>Rename</Typography>,
      onClick: openChatRenameModal,
    },
    {
      icon: (
        <ArchiveRounded
          fontSize="small"
          sx={{
            color: 'text.secondary',
          }}
        />
      ),
      label: <Typography>Archive</Typography>,
      onClick: openChatArchiveModal,
    },
  ];

  return (
    <KebabMenu
      menuItems={kebabMenuItems}
      buttonProps={{
        color: 'primary',
        size: 'small',
        ...kebabMenuButtonProps,
      }}
      onClose={onClose}
      menuProps={{
        PaperProps: {
          sx: {
            minWidth: '150px',
          },
        },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
      }}
    />
  );
};
